'use client';

import React, { useState } from 'react';
import { ArrowLeft, Phone, Bell, BellOff, Volume2, VolumeX, ChevronRight, Shield, Trash2 } from 'lucide-react';

interface ProfileScreenProps {
  onBack: () => void;
}

export default function ProfileScreen({ onBack }: ProfileScreenProps) {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [muteUntil, setMuteUntil] = useState<string | null>(null);

  return (
    <div
      className="flex flex-col h-screen w-full overflow-hidden"
      style={{ background: 'var(--background)' }}
    >
      {/* Header */}
      <header
        className="chat-header-surface flex items-center gap-2.5 px-2 sm:px-3 flex-shrink-0 z-20 relative"
        style={{ height: '64px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      >
        <button
          onClick={onBack}
          className="flex items-center justify-center w-[44px] h-[44px] rounded-full hover:bg-white/8 transition-colors duration-150 active:scale-95 flex-shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft size={22} className="text-white" />
        </button>
        <h1 className="text-[17px] font-semibold text-white tracking-[0.01em]">Contact Info</h1>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent', WebkitOverflowScrolling: 'touch' }}>

        {/* Profile hero */}
        <div
          className="flex flex-col items-center pt-8 pb-6 px-4"
          style={{ background: 'linear-gradient(180deg, rgba(40,127,216,0.12) 0%, transparent 100%)' }}
        >
          {/* Avatar */}
          <div className="relative mb-4">
            <div
              className="w-[96px] h-[96px] rounded-full flex items-center justify-center text-white font-bold text-[32px] shadow-lg"
              style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }}
              aria-label="Ate Boni profile picture"
            >
              AB
            </div>
            {/* Online dot */}
            <span
              className="absolute bottom-[4px] right-[4px] w-[16px] h-[16px] bg-green-400 rounded-full border-2"
              style={{ borderColor: 'var(--background)' }}
              aria-label="Online"
            />
          </div>

          {/* Name */}
          <h2 className="text-[22px] font-bold text-white tracking-[0.01em] mb-1">Ate Boni</h2>
          {/* Status */}
          <p className="text-[13px]" style={{ color: '#91A3B7' }}>Online</p>
        </div>

        {/* Info cards */}
        <div className="px-4 space-y-3 pb-4">

          {/* Phone */}
          <InfoCard>
            <InfoRow
              icon={<Phone size={18} style={{ color: 'var(--primary)' }} />}
              label="Phone"
              value="+63 912 345 6789"
            />
          </InfoCard>

          {/* Bio */}
          <InfoCard>
            <div className="px-4 py-3.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest mb-1.5" style={{ color: '#91A3B7' }}>Bio</p>
              <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                ✨ Living life one message at a time. Designer by day, dreamer by night. 🌙
              </p>
            </div>
          </InfoCard>

          {/* Notifications section */}
          <SectionLabel>Notifications</SectionLabel>
          <InfoCard>
            <ToggleRow
              icon={notificationsEnabled
                ? <Bell size={18} style={{ color: 'var(--primary)' }} />
                : <BellOff size={18} style={{ color: '#91A3B7' }} />}
              label="Notifications"
              description={notificationsEnabled ? 'You will receive notifications' : 'Notifications are muted'}
              enabled={notificationsEnabled}
              onToggle={() => setNotificationsEnabled(v => !v)}
            />
            <Divider />
            <ToggleRow
              icon={soundEnabled
                ? <Volume2 size={18} style={{ color: 'var(--primary)' }} />
                : <VolumeX size={18} style={{ color: '#91A3B7' }} />}
              label="Message Sound"
              description={soundEnabled ? 'Play sound for new messages' : 'Sound is off'}
              enabled={soundEnabled}
              onToggle={() => setSoundEnabled(v => !v)}
            />
            <Divider />
            <button
              className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/5 transition-colors duration-150 active:bg-white/8"
              onClick={() => setMuteUntil(muteUntil ? null : '8 hours')}
            >
              <span className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <BellOff size={16} style={{ color: muteUntil ? '#91A3B7' : 'var(--primary)' }} />
              </span>
              <div className="flex-1 text-left">
                <p className="text-[14px] font-medium text-white">Mute Notifications</p>
                <p className="text-[12px] mt-0.5" style={{ color: '#91A3B7' }}>
                  {muteUntil ? `Muted for ${muteUntil}` : 'Temporarily silence alerts'}
                </p>
              </div>
              <ChevronRight size={16} style={{ color: '#91A3B7' }} />
            </button>
          </InfoCard>

          {/* Privacy & danger zone */}
          <SectionLabel>Privacy</SectionLabel>
          <InfoCard>
            <button className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/5 transition-colors duration-150 active:bg-white/8">
              <span className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <Shield size={16} style={{ color: 'var(--primary)' }} />
              </span>
              <div className="flex-1 text-left">
                <p className="text-[14px] font-medium text-white">Encryption</p>
                <p className="text-[12px] mt-0.5" style={{ color: '#91A3B7' }}>Messages are end-to-end encrypted</p>
              </div>
              <ChevronRight size={16} style={{ color: '#91A3B7' }} />
            </button>
          </InfoCard>

          <InfoCard>
            <button className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-red-500/10 transition-colors duration-150 active:bg-red-500/15">
              <span className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.12)' }}>
                <Trash2 size={16} className="text-red-400" />
              </span>
              <div className="flex-1 text-left">
                <p className="text-[14px] font-medium text-red-400">Delete Chat</p>
                <p className="text-[12px] mt-0.5" style={{ color: '#91A3B7' }}>Remove all messages permanently</p>
              </div>
            </button>
          </InfoCard>

          {/* Bottom spacing */}
          <div className="h-6" />
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-widest px-1 pt-1" style={{ color: '#91A3B7' }}>
      {children}
    </p>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <span className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: '#91A3B7' }}>{label}</p>
        <p className="text-[14px] font-medium text-white truncate">{value}</p>
      </div>
    </div>
  );
}

function ToggleRow({
  icon, label, description, enabled, onToggle,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <span className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-white">{label}</p>
        <p className="text-[12px] mt-0.5" style={{ color: '#91A3B7' }}>{description}</p>
      </div>
      <button
        onClick={onToggle}
        role="switch"
        aria-checked={enabled}
        aria-label={`Toggle ${label}`}
        className="flex-shrink-0 w-[44px] h-[26px] rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
        style={{
          background: enabled ? 'var(--primary)' : 'rgba(255,255,255,0.15)',
          focusRingColor: 'var(--primary)',
        }}
      >
        <span
          className="absolute top-[3px] w-[20px] h-[20px] rounded-full bg-white shadow transition-all duration-200"
          style={{ left: enabled ? '21px' : '3px' }}
        />
      </button>
    </div>
  );
}

function Divider() {
  return <div className="mx-4" style={{ height: '1px', background: 'var(--border)' }} />;
}
